import { ApolloError, useQuery } from '@apollo/client';
import { createContext, useCallback, useContext } from 'react';
import { Queries } from '@shared/api';
import { useUpdateWorkflowEntity as useUpdateWorkflowEntityMutation } from '@shared/scenes/entity/edit/hooks/useUpdateWorkflowEntity';

import { API } from '@shared/types/api';
export interface WorkflowEntityState {
  workflowEntity: API.WorkflowEntity | null;
  updateField: (field: string, value: any) => Promise<API.WorkflowEntity | undefined>;
  loading: boolean;
  error: ApolloError | undefined;
}

const initialState: WorkflowEntityState = {
  workflowEntity: null,
  updateField: async () => undefined,
  loading: false,
  error: undefined,
};

export const WorkflowEntityContext = createContext(initialState);

function useUpdateWorkflowEntity() {
  const { updateWorkflowEntity } = useUpdateWorkflowEntityMutation();

  return useCallback(
    async (workflowEntity, fields) => {
      const { data } = await updateWorkflowEntity(workflowEntity.id, fields);

      return data?.updateWorkflowEntity?.entity;
    },
    [updateWorkflowEntity],
  );
}

export function useUpdateField() {
  const { updateField } = useContext(WorkflowEntityContext);
  return useCallback(updateField, [updateField]);
}

export function useField(fieldName) {
  const { workflowEntity } = useContext(WorkflowEntityContext);
  return workflowEntity?.fields?.[fieldName] ?? null;
}

export function useLoadingAndError() {
  const { loading, error } = useContext(WorkflowEntityContext);
  return { loading, error };
}

function useGetWorkflowEntity(entityId) {
  return useQuery<API.GetWorkflowEntityQuery, API.GetWorkflowEntityQueryVariables>(
    Queries.workflow.getWorkflowEntity,
    { variables: { input: { id: entityId } } },
  );
}

export function WorkflowEntityProvider({ entityId, children }) {
  const { data, error, loading } = useGetWorkflowEntity(entityId);
  const updateWorkflowEntity = useUpdateWorkflowEntity();
  const workflowEntity = data?.workflowEntity?.entity ?? null;

  const updateField = useCallback(
    async (field: string, value: any) => {
      const updatedWorkflowEntity = await updateWorkflowEntity(workflowEntity, {
        [field]: value,
      });
      return updatedWorkflowEntity;
    },
    [workflowEntity, updateWorkflowEntity],
  );

  return (
    <WorkflowEntityContext.Provider value={{ workflowEntity, loading, error, updateField }}>
      {children}
    </WorkflowEntityContext.Provider>
  );
}
